import {
  SeaAllFilterField,
  useTagActions,
} from '@aibees/elephant/components/see-all-popup';
import {
  useCompanyReadDataContext,
  usePersonReadDataContext,
} from '@aibees/elephant/context';
import { CompanyDescription } from '@aibees/elephant/feature/search-drawer/components/company-description';
import { CompanySeoDescription } from '@aibees/elephant/feature/search-drawer/components/company-seo-description copy';
import { CompanyOverview } from '@aibees/elephant/feature/search-drawer/feature/overview/components/company-overview';
import { FinancesInfo } from '@aibees/elephant/feature/search-drawer/feature/overview/components/finances-info';
import { useIsFinancialInfoEmpty } from '@aibees/elephant/feature/search-drawer/feature/overview/components/finances-info/use-is-financial-info-empty';
import { IndustryInfo } from '@aibees/elephant/feature/search-drawer/feature/overview/components/industry-info';
import {
  useCompanyVisibilityState,
  useDisplayCompanyName,
} from '@aibees/elephant/hooks';
import { ROUTES as R } from '@aibees/elephant/router/routes';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Divider } from '@mui/material';
import { memo } from 'react';
import { getCompanyInfoSectionVisibility } from '../company-info/utils';
import { CompanyProfile } from '../company-profile';
import { CurrentCompanyContactData } from '../current-company-contact-data';
import { EntityInfoPanel } from '../entity-info-panel';
import { ShowMoreButton } from '../show-more-button';
import * as SX from './sx';
import type { CurrentCompanyProps as Props } from './types';

export const CurrentCompany = memo(
  ({ className, isSidebar, companyName }: Props) => {
    const { api } = useCompanyReadDataContext();
    const personContext = usePersonReadDataContext();
    const actions = useTagActions();
    const isFinancialInfoEmpty = useIsFinancialInfoEmpty();
    const showMore = useBoolean();
    const { showOverview, showSeo } = getCompanyInfoSectionVisibility(
      api?.data
    );
    const {
      shouldShowPreviousEmployment: shouldShowPrevious,
      hasFullCompanyData,
      displayCurrentCompanyButtons,
    } = useCompanyVisibilityState();

    const { displayName: displayCompanyName } = useDisplayCompanyName(
      personContext?.api?.data,
      api?.data?.summary?.name ?? companyName
    );

    const title = shouldShowPrevious ? 'Previous Company' : 'Current Company';

    const handleViewFullProfile = () => {
      if (api?.data?.id) {
        window.open(R.company.replace(':id', api?.data?.id), '_blank');
      }
    };

    return (
      <EntityInfoPanel
        className={classnames('CurrentCompany', className)}
        titleSx={{
          width: '100%',
        }}
        title={title}
        icon="business"
        sx={{ gap: 0 }}
      >
        <Box sx={SX.searchCompanyNameWrapper}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'end',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'visible' }}>
              <CompanyProfile
                name={displayCompanyName}
                imageSrc={api?.data?.summary?.logo?.source || ''}
                id={api?.data?.id}
                socialLink={api?.data?.link}
                avatarSize={48}
                showPopover
                badgeSize={22}
              />
            </Box>
            {(api?.data?.id ||
              displayCompanyName ||
              displayCurrentCompanyButtons) && (
              <Box
                sx={{
                  alignItems: 'center',
                  gap: 0.5,
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  size="xsmall"
                  onClick={handleViewFullProfile}
                  disabled={!hasFullCompanyData}
                >
                  Full Profile
                </Button>
                {(isSidebar ||
                  !!displayCompanyName ||
                  displayCurrentCompanyButtons) && (
                  <CurrentCompanyContactData
                    iconOrder="vertical"
                    size="medium"
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {hasFullCompanyData && <Divider />}

        <Box>
          {showOverview && hasFullCompanyData && (
            <CompanyDescription sx={SX.description} />
          )}
          {showSeo && hasFullCompanyData && (
            <CompanySeoDescription sx={SX.description} />
          )}
          {hasFullCompanyData && <CompanyOverview hideHeader />}
          {showMore.value && hasFullCompanyData && (
            <>
              <Divider />
              <FinancesInfo hideHeader />

              {!isFinancialInfoEmpty && <Divider />}

              <IndustryInfo
                config={{
                  actions,
                  field: SeaAllFilterField.industry,
                  placeholder: 'Search Industry Tags',
                }}
                hideHeader
              />
            </>
          )}

          {hasFullCompanyData && (
            <Box sx={{ p: 1 }}>
              <ShowMoreButton onClick={(value) => showMore.setValue(value)} />
            </Box>
          )}
        </Box>
      </EntityInfoPanel>
    );
  }
);

CurrentCompany.displayName = 'CurrentCompany';
